/** 導師入駐：錄音與上傳操作手冊 */
export default {
	common: {
		kicker: "操作說明",
		title: "錄音與上傳檔案操作手冊",
		summary:
			"請依照裝置類型選擇對應章節。錄音完成後，請確認音質、格式、大小與網路狀態，再上傳錄音檔案。",
		close: "關閉",
	},
	sidePanel: {
		keyReminders: {
			title: "快速重點",
			items: [
				"錄音前確認麥克風可正常收音。",
				"選擇安靜環境，避免長時間空白。",
				"檔名建議包含姓名與日期。",
				"上傳後以頁面成功提示為準。",
			],
		},
		formats: {
			title: "建議格式",
			chips: ["MP3", "M4A", "AAC", "MP4", "3GP", "3G2"],
		},
		fileSize: {
			title: "檔案大小",
			items: ["最高支援 5MB。", "若檔案過大，請壓縮或重新錄製後再上傳。"],
		},
	},
	checklist: {
		title: "上傳前檢查清單",
		rows: [
			{
				label: "音質",
				value: "可清楚聽到主要說話內容，沒有長時間空白或過大雜音。",
			},
			{
				label: "格式",
				value: "支援 MP3、M4A、AAC、MP4、3GP、3G2 等格式，建議優先使用 MP3 或 M4A。",
			},
			{
				label: "大小",
				value: "檔案大小最高支援 5MB。若檔案過大，請依平台要求重新壓縮後再上傳。",
			},
			{
				label: "網路",
				value: "上傳時保持網路穩定，避免中途關閉頁面。",
			},
		],
	},
	faq: {
		title: "常見問題處理",
		items: [
			{
				label: "找不到錄音檔：",
				text: "先查看錄音 App 的分享、匯出或檔案位置功能。",
			},
			{
				label: "無法上傳：",
				text: "確認檔案格式與大小是否符合平台限制。",
			},
			{
				label: "錄音聲音太小：",
				text: "重新錄音時靠近麥克風，並選擇安靜環境。",
			},
			{
				label: "上傳後不確定是否成功：",
				text: "請留意頁面提示；如仍不確定，可重新上傳一次。",
			},
		],
	},
	mobile: {
		tabs: {
			iphone: "iPhone 錄音",
			android: "Android 錄音",
			computer: "電腦錄音",
			checklist: "檢查清單",
			faq: "常見問題",
		},
		iphone: {
			title: "iPhone 錄音",
			note: "建議錄音時靠近收音位置，並盡量避開背景噪音。檔名可使用「姓名_日期_錄音」，方便後續辨識。",
			steps: [
				{
					paragraphs: ["打開「語音備忘錄」App。"],
					imageKey: "step1",
					imageLayout: "single",
					imageAlts: ["打開語音備忘錄 App"],
				},
				{
					paragraphs: ["點選紅色錄音按鈕開始錄音。", "錄音時請靠近收音位置，避免背景噪音過大。"],
					imageKey: "step2",
					imageLayout: "single",
					imageAlts: ["點選紅色錄音按鈕"],
				},
				{
					paragraphs: ["完成後點選停止按鈕。"],
					imageKey: "step3",
					imageLayout: "single",
					imageAlts: ["點選停止按鈕"],
				},
				{
					paragraphs: [
						"此步驟非必要：點選錄音名稱，將檔名修改為容易辨識的名稱。",
						"範例：姓名_日期_錄音。",
					],
					imageKey: "step4",
					imageLayout: "row",
					imageAlts: ["修改錄音名稱第一步", "修改錄音名稱第二步"],
				},
				{
					paragraphs: ["錄音完成後，請將錄音檔案儲存至手機檔案夾。"],
					subSteps: [
						"點擊「選擇」按鈕。",
						"選中剛才錄好的錄音，並點擊分享按鈕。",
						"點擊「儲存到檔案」按鈕。",
						"點擊「儲存」按鈕，即可將錄音檔案儲存至手機檔案夾。",
					],
					imageKey: "step5",
					imageLayout: "row",
					imageAlts: ["點擊選擇按鈕", "選中錄音並分享", "儲存到檔案", "完成儲存"],
				},
				{
					paragraphs: [
						"確認檔案已成功儲存後，點選頁面中的「點擊上傳」按鈕，再點擊「選擇檔案」。",
						"選擇剛才錄製並儲存的錄音檔案。當頁面顯示「錄音上傳成功」時，即表示上傳完成。若需要重新上傳，請先點擊刪除圖示移除原檔案，再重新選擇並上傳錄音檔案。",
					],
					imageKey: "step6",
					imageLayout: "row",
					imageAlts: ["點擊上傳按鈕", "錄音上傳成功", "移除錄音檔案"],
				},
			],
		},
		android: {
			title: "Android 手機錄音",
			note: "以下操作以三星手機為例。不同品牌手機的錄音 App 名稱可能略有不同，請以實際頁面為準。",
			steps: [
				{
					paragraphs: ["打開手機內建的「錄音機」、「語音錄製」或類似名稱的 App。"],
					imageKey: "step1",
					imageLayout: "single",
					imageAlts: ["打開 Android 錄音 App"],
				},
				{
					paragraphs: ["點選錄音按鈕開始錄音。", "錄音時請保持手機穩定，並盡量在安靜環境中進行。"],
					imageKey: "step2",
					imageLayout: "single",
					imageAlts: ["Android 點選錄音按鈕"],
				},
				{
					paragraphs: ["完成後點選停止或儲存。"],
					imageKey: "step3",
					imageLayout: "single",
					imageAlts: ["Android 完成錄音"],
				},
				{
					paragraphs: ["此步驟非必要：將檔案重新命名為容易辨識的名稱。", "範例：姓名_日期_錄音。"],
					imageKey: "step4",
					imageLayout: "row",
					imageAlts: ["Android 重新命名第一步", "Android 重新命名第二步", "Android 重新命名完成"],
				},
				{
					paragraphs: [
						"確認檔案已成功儲存後，點選頁面中的「點擊上傳」按鈕，依照頁面提示選擇對應的錄音檔案。",
						"當頁面顯示「錄音上傳成功」時，即表示上傳完成。若需要重新上傳，請先點擊刪除圖示移除原檔案，再重新選擇並上傳錄音檔案。",
					],
					imageKey: "step5",
					imageLayout: "row",
					imageAlts: [
						"點擊上傳按鈕",
						"Android 選擇檔案第一步",
						"Android 選擇檔案第二步",
						"Android 選擇錄音檔案",
						"Android 錄音上傳成功",
						"移除錄音檔案",
					],
				},
				{
					paragraphs: [
						"特別說明：部分 Android 手機點選「點擊上傳」後，可直接使用系統錄音機錄音並上傳。",
						"若您的手機支援此功能，可直接點選「錄音機」按鈕，錄製 60 至 300 秒的錄音後上傳。",
					],
					imageKey: "step6",
					imageLayout: "single",
					imageAlts: ["Android 直接錄音並上傳"],
				},
			],
		},
	},
	pc: {
		tabs: {
			iphone: "iPhone 錄音",
			android: "Android 錄音",
			computer: "電腦錄音",
			checklist: "檢查清單",
			faq: "常見問題",
		},
		computer: {
			title: "電腦錄音",
			note: "更建議使用手機錄音並上傳；若需使用電腦錄音，請先確認麥克風與瀏覽器上傳功能可正常使用。",
			windows: {
				title: "Windows 電腦錄音",
				steps: [
					{ paragraphs: ["在開始選單搜尋並打開「錄音機」或「Sound Recorder」。"] },
					{ paragraphs: ["確認麥克風已連接並可正常使用。"] },
					{ paragraphs: ["點選錄音按鈕開始錄音。"] },
					{ paragraphs: ["完成後點選停止，系統會自動儲存錄音檔。"] },
					{ paragraphs: ["在錄音清單中找到該檔案，重新命名為容易辨識的名稱。"] },
					{
						paragraphs: [
							"確認檔案已成功儲存後，點選頁面中的「點擊上傳」按鈕，依照頁面提示選擇對應的錄音檔案。",
							"當頁面顯示「錄音上傳成功」時，即表示上傳完成。若需要重新上傳，請先點擊刪除圖示移除原檔案，再重新選擇並上傳錄音檔案。",
						],
						imageKey: "upload",
						imageLayout: "row",
						imageAlts: ["電腦端上傳錄音檔案", "電腦端選擇錄音檔案", "電腦端錄音上傳成功"],
					},
				],
			},
			mac: {
				title: "Mac 電腦錄音",
				steps: [
					{ paragraphs: ["打開「QuickTime Player」。"] },
					{ paragraphs: ["在上方選單選擇「檔案」>「新增音訊錄製」。"] },
					{ paragraphs: ["點選錄音按鈕開始錄音。"] },
					{ paragraphs: ["完成後點選停止。"] },
					{
						paragraphs: ["選擇「檔案」>「儲存」，輸入清楚的檔案名稱並選擇儲存位置。"],
					},
					{
						paragraphs: [
							"確認檔案已成功儲存後，點選頁面中的「點擊上傳」按鈕，依照頁面提示選擇對應的錄音檔案。",
							"當頁面顯示「錄音上傳成功」時，即表示上傳完成。若需要重新上傳，請先點擊刪除圖示移除原檔案，再重新選擇並上傳錄音檔案。",
						],
						imageKey: "macUpload",
						imageLayout: "row",
						imageAlts: ["電腦端上傳錄音檔案", "電腦端選擇錄音檔案", "電腦端錄音上傳成功"],
					},
				],
			},
		},
	},
};
